import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from "@iconify/react";
import Datetime from 'react-datetime';
import "react-datetime/css/react-datetime.css";
import defaultLogo from 'assets/images/logos/defaultlogo.png';

const EventsBookingTable = ({ title, rows, role, onActionClick, options, handleDialogBoxes, totalCount, pagesCount, currentPage, rowsPerPage, onRowsPerPageChange, onPageChange, onStatusChange, eventStatus, categories, category, onCategoryChange, onDateChange, filteredDate, onSearch, searcItem, isArchivedStatus, onToggle, loaderStatus, addButtonTitle }) => {
    const { showHeader, showHeaderTwo, viewOption, addOption, statusFilterOption, dateFilterOption, typeFilterOption, searchOption, archiveFilterOption } = options;
    // const { userData } = useContext(UserDataContext);
    const isAdmin = role === 'admin' ? true : false;
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(eventStatus || "Status");

    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(filteredDate || null);

    const [isTypeOpen, setIsTypeOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(category || "Type");

    const [isPaginationOpen, setIsPaginationOpen] = useState(false);
    const [selectedPagination, setSelectedPagination] = useState(rowsPerPage.toString());
    const [isArchived, setIsArchived] = useState(null || isArchivedStatus);
    const [searchTerm, setSearchTerm] = useState(searcItem || '');
    const searchTimeoutRef = useRef(null);

    const handleSearch = (event) => {
        const value = event.target.value;
        setSearchTerm(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set a new timeout to debounce search
        searchTimeoutRef.current = setTimeout(() => {
            onSearch(value);
        }, 500); // Adjust debounce time if needed
    };

    const handlePaginationSelect = (count) => {
        setSelectedPagination(count);
        setIsPaginationOpen(false);
        onRowsPerPageChange(Number(count)); // Notify parent about the change
    };

    const handlePageChange = (pageNumber) => {
        onPageChange(pageNumber); // Notify parent about the current page change
    };

    const formattedDate = (date) => {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(date).toLocaleDateString('en-GB', options);
    };

    const formatTime = (timeStr) => {
        return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-lg mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full gap-4 mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            </div>

            {showHeader && (
                <div className="flex flex-wrap items-center gap-3 py-4 border-b border-t px-4 md:px-6">
                    {/* Filters Section (Wraps into multiple rows if needed) */}
                    <div className="flex flex-wrap items-center gap-3 flex-grow">
                        {/* Pagination Dropdown */}
                        <div className="flex items-center space-x-2 relative">
                            <span className="text-gray-600">Show</span>
                            <div
                                className="relative border border-gray-300 rounded-lg px-4 w-20 h-10 flex items-center justify-between cursor-pointer focus:ring-2 focus:ring-gray-200"
                                onClick={() => setIsPaginationOpen((prev) => !prev)}
                            >
                                <span>{selectedPagination}</span>
                                <Icon icon="iconamoon:arrow-down-2-thin" className="text-xl" />
                            </div>
                            {isPaginationOpen && (
                                <ul className="absolute left-12 top-full z-10 bg-white border border-gray-200 rounded-lg mt-1 w-20 shadow-lg max-h-60 overflow-y-auto">
                                    {["5", "10", "15", "20", "30", "40", "50"].map((count) => (
                                        <li
                                            key={count}
                                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-center"
                                            onClick={() => handlePaginationSelect(count)}
                                        >
                                            {count}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Search Input */}
                        {searchOption && (
                            <input
                                type="text"
                                placeholder="Search here"
                                value={searchTerm}
                                onChange={handleSearch}
                                className="border border-gray-300 rounded-lg px-4 h-10 text-sm focus:ring-2 focus:ring-gray-200 w-48 sm:w-56 md:w-64 lg:w-80"
                            />
                        )}
                    </div>
                </div>
            )}

            <div className="space-y-4 mt-4 mb-4">
                {loaderStatus ? (
                    Array.from({ length: 4 }).map((_, idx) => (
                        <div key={`skeleton-${idx}`} className="bg-gray-100 rounded-lg overflow-hidden animate-pulse">
                            <div className="flex items-center p-4">
                                <div className="flex-shrink-0 w-16 h-16 bg-gray-300 rounded-md" />

                                <div className="flex-grow px-4 space-y-2">
                                    <div className="h-4 bg-gray-300 rounded w-3/4" />
                                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                                </div>

                                <div className="flex-shrink-0 ml-2">
                                    <div className="h-8 w-20 bg-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                    ))
                ) : rows.length <= 0 ? (
                    <div className="text-center py-4 text-gray-500">
                        No {title} Available...
                    </div>
                ) : (
                    rows.map((row, index) => (
                        <div key={index} className="bg-gray-100 rounded-lg overflow-hidden">
                            <div className="flex items-center p-4">
                                <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center text-white">
                                    <img src={row.coverImage?.secure_url || defaultLogo} alt={`${row.name} cover`} className="w-12 h-12 rounded-md object-cover" />
                                </div>

                                <div className="flex-grow px-4">
                                    <div className="font-semibold">{row.name}</div>
                                    <div className="text-sm text-gray-600">{formattedDate(row.eventDateTimes[0]?.date)} | {formatTime(row.eventDateTimes[0]?.startTime)} | {row.venue?.name || ''}</div>
                                </div>

                                <div className="flex-shrink-0 ml-2">
                                    <button
                                        disabled={row.isBookingClosed}
                                        className={`flex items-center justify-center px-2 py-1 rounded-md ${row.isBookingClosed ? 'opacity-50 cursor-not-allowed bg-gray-300' : 'bg-blue-500 text-white rounded-md hover:bg-blue-600'}`}
                                        onClick={() => onActionClick && onActionClick(row, 'view')}
                                    >
                                        Book Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing
                            {/* <span className="font-medium">1</span>
              to */}
                            <span className="font-medium ml-1 mr-1">{rowsPerPage}</span>
                            of
                            <span className="font-medium ml-1 mr-1">{totalCount}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <div
                            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                            aria-label="Pagination"
                        >
                            {/* Previous Button */}
                            <button
                                onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                            >
                                <span className="sr-only">Previous</span>
                                <svg
                                    className="size-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>

                            {/* Dynamically Generated Page Buttons */}
                            {Array.from({ length: pagesCount }, (_, index) => {
                                const page = index + 1;
                                return (
                                    <button
                                        key={page}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePageChange(page);
                                        }}
                                        aria-current={currentPage === page ? "page" : undefined}
                                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === page
                                            ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                            : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                            }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            {/* Next Button */}
                            <button
                                onClick={() => currentPage < pagesCount && handlePageChange(currentPage + 1)}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                            >
                                <span className="sr-only">Next</span>
                                <svg
                                    className="size-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

// Prop Validation
EventsBookingTable.propTypes = {
    /** Array of data objects to populate rows */
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    title: PropTypes.string.isRequired,
    role: PropTypes.string,

    options: PropTypes.objectOf(
        PropTypes.shape({
            showHeader: PropTypes.bool.isRequired,
            showHeaderTwo: PropTypes.bool.isRequired,
            viewOption: PropTypes.bool.isRequired,
            addOption: PropTypes.bool.isRequired,
            statusFilterOption: PropTypes.bool.isRequired,
            dateFilterOption: PropTypes.bool.isRequired,
            typeFilterOption: PropTypes.bool.isRequired,
            searchOption: PropTypes.bool.isRequired,
            archiveFilterOption: PropTypes.bool.isRequired,
        })
    ).isRequired,

    totalCount: PropTypes.number,
    currentPage: PropTypes.number,
    rowsPerPage: PropTypes.number,
    onRowsPerPageChange: PropTypes.func,
    onPageChange: PropTypes.func,
    pagesCount: PropTypes.number,
    onStatusChange: PropTypes.func,
    eventStatus: PropTypes.string,
    categories: PropTypes.array,
    category: PropTypes.string,
    onCategoryChange: PropTypes.func,
    onDateChange: PropTypes.func,
    filteredDate: PropTypes.string,
    onSearch: PropTypes.func,
    searcItem: PropTypes.string,
    onToggle: PropTypes.func,
    isArchivedStatus: PropTypes.bool,
    loaderStatus: PropTypes.bool,
    addButtonTitle: PropTypes.string,
    // category: PropTypes.string,

    /** Optional function to handle actions on rows */
    onActionClick: PropTypes.func,
    handleDialogBoxes: PropTypes.func,
};

EventsBookingTable.defaultProps = {
    onActionClick: null,
    handleDialogBoxes: null,
};

export default EventsBookingTable;
